var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef } from 'react';
import { css } from '@emotion/react';
import styled from '../../styles/styled';
import { uniqueId } from '../../util/id';
import { Body } from '../Body/Body';
import { AccessibilityError } from '../../util/errors';
import { FieldDescription, FieldWrapper } from '../FieldAtoms';
import { CLASS_DISABLED } from '../FieldAtoms/constants';
import { hideVisually } from '../../styles/style-mixins';
import { Switch } from './components/Switch/Switch';
const labelStyles = ({ theme }) => css `
  display: block;
  margin-left: ${theme.spacings.kilo};
  cursor: pointer;

  ${theme.mq.untilKilo} {
    margin-left: 0;
    margin-right: ${theme.spacings.kilo};
  }

  .${CLASS_DISABLED} & {
    color: var(--cui-fg-normal-disabled);
  }
`;
// This component is rendered as a `label` element below.
const ToggleLabel = styled(Body)(labelStyles);
const wrapperStyles = (theme) => css `
  display: flex;
  align-items: flex-start;

  ${theme.mq.untilKilo} {
    flex-direction: row-reverse;
    justify-content: space-between;
  }
`;
/**
 * A toggle component with support for labels and additional explanations.
 */
export const Toggle = forwardRef((_a, ref) => {
    var { label, explanation, description, 'aria-describedby': describedBy } = _a, props = __rest(_a, ["label", "explanation", "description", 'aria-describedby']);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test' &&
        !label) {
        throw new AccessibilityError('Toggle', 'The `label` prop is missing.');
    }
    const switchId = uniqueId('toggle-switch_');
    const labelId = uniqueId('toggle-label_');
    const descriptionId = description || explanation ? uniqueId('toggle-explanation_') : '';
    const descriptionIds = [describedBy, descriptionId]
        .filter(Boolean)
        .join(' ');
    return (_jsxs(FieldWrapper, Object.assign({ disabled: props.disabled, css: wrapperStyles }, { children: [_jsx(Switch, Object.assign({}, props, { "aria-labelledby": labelId, "aria-describedby": descriptionIds, id: switchId, ref: ref })), _jsxs(ToggleLabel, Object.assign({ as: "label", id: labelId, htmlFor: switchId }, { children: [label, (description || explanation) && (_jsx(FieldDescription, Object.assign({ "aria-hidden": "true" }, { children: description || explanation })))] })), (description || explanation) && (_jsx("p", Object.assign({ id: descriptionId, css: hideVisually }, { children: description || explanation })))] })));
});
Toggle.displayName = 'Toggle';
